import React, { useState } from 'react';
import { Send, Github, Linkedin, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { personalInfo } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const recipient = 'eucapop@gmail.com';
    const subjectLine = formData.subject
      ? `Portfolio contact: ${formData.subject}`
      : 'Portfolio contact';
    const bodyLines = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      '',
      formData.message,
    ];
    const mailtoUrl = `mailto:${recipient}`
      + `?subject=${encodeURIComponent(subjectLine)}`
      + `&body=${encodeURIComponent(bodyLines.join('\n'))}`;

    // Use location.href so the OS mail handler is invoked without
    // opening a blank tab in browsers without a registered handler.
    window.location.href = mailtoUrl;

    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
    }, 6000);
  };

  return (
    <main id="main-content" className="pt-24 sm:pt-32 pb-24">
      <div className="w-full max-w-[1100px] mx-auto px-6 sm:px-8">
        {/* Header */}
        <section className="mb-16">
          <h1 className="text-[clamp(32px,6vw,56px)] leading-[1.15] font-bold tracking-[-0.01em] mb-6">
            Get in touch
          </h1>
          <p className="text-lg text-muted-foreground leading-[1.6] max-w-[68ch]">
            Based in NYC, open to remote opportunities. Send me a message and I'll respond within 24-48 hours.
          </p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-border">
              <CardContent className="p-8">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center space-y-3">
                    <Mail className="h-12 w-12 text-accent" />
                    <h3 className="text-xl font-bold">Opening your email app...</h3>
                    <p className="text-muted-foreground max-w-[42ch]">
                      Please review the message and send it from your email client.
                      If nothing opened, email me directly using the address below.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-medium">
                          Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="focus-visible:ring-accent"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium">
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your.email@example.com"
                          className="focus-visible:ring-accent"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-sm font-medium">
                        Subject *
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="What's this about?"
                        className="focus-visible:ring-accent"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-sm font-medium">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell me about your project or inquiry..."
                        rows={8}
                        className="focus-visible:ring-accent resize-none"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full sm:w-auto group bg-accent hover:bg-accent-strong text-accent-foreground font-medium"
                    >
                      <Send className="mr-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-[var(--dur-2)]" />
                      Send message
                    </Button>
                  </form>
                )}

                <p className="text-sm text-muted-foreground mt-4">
                  Prefer direct email?{' '}
                  <a
                    href="mailto:eucapop@gmail.com"
                    className="text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
                  >
                    eucapop@gmail.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Social Links */}
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold mb-4 tracking-[-0.01em]">Connect</h3>
              <p className="text-muted-foreground text-sm leading-[1.6] mb-6">
                Find me on these platforms or send a message using the form.
              </p>
              
              <div className="space-y-4">
                <a
                  href={personalInfo.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-muted-foreground hover:text-accent transition-colors duration-[var(--dur-2)] group"
                >
                  <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:border-accent transition-colors duration-[var(--dur-2)]">
                    <Github className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">GitHub</p>
                    <p className="text-xs text-muted-foreground">View my code</p>
                  </div>
                </a>
                
                <a
                  href={personalInfo.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-muted-foreground hover:text-accent transition-colors duration-[var(--dur-2)] group"
                >
                  <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center group-hover:border-accent transition-colors duration-[var(--dur-2)]">
                    <Linkedin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">LinkedIn</p>
                    <p className="text-xs text-muted-foreground">Professional network</p>
                  </div>
                </a>
              </div>
            </div>

            <div className="p-6 rounded-[var(--r2)] bg-card border border-border">
              <h4 className="text-sm font-semibold mb-2 tracking-[-0.01em]">Response time</h4>
              <p className="text-sm text-muted-foreground leading-[1.6]">
                I typically respond within 24-48 hours. For urgent matters, feel free to reach out on LinkedIn.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Contact;
